import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { CustomerTimelineEntry } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useCustomerTimeline } from '../hooks/use-customer-timeline';

/** "order.payment_received" → "Payment received", for events the locale file does not name yet. */
function humanize(event: string): string {
  const tail = event.slice(event.indexOf('.') + 1).replace(/_/g, ' ');
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}

function Entry({ storeId, entry }: { storeId: string; entry: CustomerTimelineEntry }) {
  const { t } = useTranslation('customers');
  const format = useFormat();
  const title = t(`timeline.events.${entry.event}`, { defaultValue: humanize(entry.event) });
  return (
    <li className="flex flex-col gap-1 border-l pl-3 text-sm" aria-label={title}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{t(`timeline.kinds.${entry.kind}`)}</Badge>
        <span className="font-medium">{title}</span>
        {entry.orderId && entry.orderName ? (
          <Link
            to="/s/$storeId/orders/$orderId"
            params={{ storeId, orderId: entry.orderId }}
            className="text-primary hover:underline"
          >
            {entry.orderName}
          </Link>
        ) : null}
      </div>
      {entry.message ? <p className="text-muted-foreground">{entry.message}</p> : null}
      <p className="text-muted-foreground text-xs">
        <time dateTime={entry.occurredAt} title={format.utc(entry.occurredAt)}>
          {format.dateTime(entry.occurredAt)}
        </time>
        {' · '}
        {t(`timeline.actors.${entry.actorType}`)}
      </p>
    </li>
  );
}

/** Customer changes, order events and privacy requests in one list, newest first. */
export function CustomerTimelineCard({
  storeId,
  customerId,
}: {
  storeId: string;
  customerId: string;
}) {
  const { t } = useTranslation('customers');
  const query = useCustomerTimeline(storeId, customerId);
  const entries = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('timeline.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-24" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('timeline.empty')}</p>
        ) : (
          <ol className="flex flex-col gap-3" aria-label={t('timeline.title')}>
            {entries.map((entry) => (
              <Entry key={`${entry.kind}:${entry.id}`} storeId={storeId} entry={entry} />
            ))}
          </ol>
        )}
        {query.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {t('timeline.more')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
